import React, { FC, useState } from "react";
import { observer } from "mobx-react";

import Popup from "../../Popup";
import { chatService } from "../../../../services/chatService";
import { useChatStore } from "../../../../hooks/useChatStore";
import useNotifier from "../../../../hooks/useNotifier";

interface DeleteChatConfirmationDialogProps {
  onChatDeleted: (chatName: string) => void;
}

const DeleteChatConfirmationDialog: FC<DeleteChatConfirmationDialogProps> = observer(
  ({ onChatDeleted }) => {
    const chatStore = useChatStore();
    const notifier = useNotifier();
    const [isDeleting, setIsDeleting] = useState(false);

    const chat = chatStore.showDeletePopup;

    const handleClose = () => {
      chatStore.setShowDeletePopup(null);
    };

    const handleConfirmDelete = async () => {
      if (!chat) return;
      setIsDeleting(true);

      try {
        const response = await chatService.deleteChat(chat.name);
        if (response.success) {
          onChatDeleted(chat.name);
        } else {
          notifier.showError(response?.error || "Failed to delete chat. Please try again.");
        }
      } catch (error) {
        notifier.showError(
          error instanceof Error ? error.message : "An error occurred while deleting the chat. Please try again.",
        );
      } finally {
        setIsDeleting(false);
        chatStore.setShowDeletePopup(null);
      }
    };

    return (
      <Popup
        isOpen={!!chat}
        onClose={handleClose}
        title="Delete Chat"
        message={`Are you sure you want to delete "${chat?.label || chat?.name}"? This action cannot be undone.`}
        actions={[
          {
            label: "Cancel",
            onClick: handleClose,
            variant: "secondary",
            disabled: isDeleting,
          },
          {
            label: "Delete",
            onClick: handleConfirmDelete,
            variant: "danger",
            disabled: isDeleting,
            loading: isDeleting,
          },
        ]}
      />
    );
  },
);

export default DeleteChatConfirmationDialog;
